/**
 * PendingInvitations — admin surface for cancelling pending user
 * invitations. Calls the cancel-pending-invitation edge function and
 * surfaces the structured summary so operators see exactly which
 * cleanup steps fired.
 *
 * Spec: docs/AUDIT_PROMPT_SOFT_DELETE_AGENTS_AND_CANCEL_INVITATIONS.md
 * Audit-AI: GREEN-with-implementation-caution (structured summary
 * surfaced to operator for PROD cleanup visibility).
 */

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { UserRole } from "../types/auth.types";
import {
  getPendingInvitations,
  cancelInvitation,
  type PendingInvitationRow,
  type CancelInvitationSummary,
} from "../services/auth.service";
import { QUERY_KEYS } from "../utils/constants";
import type { UUID } from "../types/common.types";

const PendingInvitations: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canCancel =
    user?.role === UserRole.SUPER_ADMIN ||
    user?.role === UserRole.ADMIN ||
    user?.role === UserRole.SUPPORT_ADMIN;

  const queryClient = useQueryClient();
  const [targetInvitation, setTargetInvitation] =
    useState<PendingInvitationRow | null>(null);
  const [resultSummary, setResultSummary] =
    useState<CancelInvitationSummary | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: invitations = [], isLoading } = useQuery({
    queryKey: ["pending-invitations"],
    queryFn: getPendingInvitations,
  });

  const cancelMutation = useMutation({
    mutationFn: (invitationId: UUID) => cancelInvitation(invitationId),
    onSuccess: (summary) => {
      setResultSummary(summary);
      setTargetInvitation(null);
      queryClient.invalidateQueries({ queryKey: ["pending-invitations"] });
      // A cancellation removes the half-onboarded profile via the
      // auth.users cascade. Refresh every surface that lists profiles
      // or support agents so Settings → Manage Users + the Manage
      // Agents modal both reflect the change without a page reload.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] });
      queryClient.invalidateQueries({
        queryKey: ["agents-with-supervisors"],
      });
      queryClient.invalidateQueries({
        queryKey: ["agents-with-supervisors-all"],
      });
    },
    onError: (err: Error) => {
      setErrorMessage(err.message);
      setTargetInvitation(null);
    },
  });

  const now = useMemo(() => Date.now(), []);

  const statusFor = (inv: PendingInvitationRow): string => {
    return new Date(inv.expires_at).getTime() < now ? "Expired" : "Pending";
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <button
        onClick={() => navigate("/support-tasks")}
        className="text-primary-600 hover:text-primary-700 flex items-center gap-1 mb-4"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        Back to Support Tasks
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Pending Invitations
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Cancel invitations that were sent to the wrong email or no longer
          needed. Cancellation revokes the invite, deletes the half-onboarded
          auth user, and frees the email for re-invite.
        </p>
      </div>

      {errorMessage && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm flex items-start justify-between gap-2">
          <span>{errorMessage}</span>
          <button
            onClick={() => setErrorMessage(null)}
            className="text-red-900"
          >
            ×
          </button>
        </div>
      )}

      {resultSummary && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-800 text-sm">
          <div className="font-medium mb-1">Cancellation complete</div>
          <ul className="text-xs space-y-0.5">
            <li>
              Email:{" "}
              <span className="font-mono">{resultSummary.email_used}</span>
            </li>
            <li>
              Resolved via:{" "}
              <span className="font-mono">{resultSummary.resolved_via}</span>
            </li>
            <li>
              Invitation row deleted: {resultSummary.user_invitations_deleted}
            </li>
            <li>
              OTP rows deleted: {resultSummary.invitation_codes_deleted_count}
            </li>
            <li>
              Auth user + profile deleted:{" "}
              {resultSummary.auth_user_deleted
                ? "yes (profile cascaded via FK)"
                : `no (${resultSummary.auth_delete_skipped_reason ?? "n/a"})`}
            </li>
            <li>
              Support agent:{" "}
              {resultSummary.support_agent_deleted
                ? "deleted"
                : `left alone (${resultSummary.support_agent_left_alone_reason ?? "n/a"})`}
            </li>
          </ul>
          <button
            onClick={() => setResultSummary(null)}
            className="mt-2 text-xs text-green-700 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow border border-gray-200">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : invitations.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No pending invitations.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Email
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Role
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Sent
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invitations.map((inv) => (
                <tr key={inv.id} className="text-sm">
                  <td className="px-4 py-2 font-mono text-gray-900">
                    {inv.email}
                  </td>
                  <td className="px-4 py-2 text-gray-700">{inv.full_name}</td>
                  <td className="px-4 py-2 text-gray-700">{inv.role}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        statusFor(inv) === "Expired"
                          ? "bg-gray-100 text-gray-700"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {statusFor(inv)}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-500 text-xs">
                    {new Date(inv.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {canCancel ? (
                      <button
                        onClick={() => {
                          setTargetInvitation(inv);
                          setResultSummary(null);
                          setErrorMessage(null);
                        }}
                        className="text-red-600 hover:text-red-700 font-medium"
                      >
                        Cancel &amp; Cleanup
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400 italic">
                        Read-only
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {targetInvitation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Cancel pending invitation
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                This will revoke the invitation for{" "}
                <span className="font-mono">{targetInvitation.email}</span>,
                delete the half-onboarded auth user (if they never signed in),
                and free the email for re-invite. The action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-2 rounded-b-lg">
              <button
                onClick={() => setTargetInvitation(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Keep invitation
              </button>
              <button
                onClick={() => cancelMutation.mutate(targetInvitation.id)}
                disabled={cancelMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {cancelMutation.isPending
                  ? "Cancelling..."
                  : "Cancel & Cleanup"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingInvitations;
